import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, X, Check, ArrowRight, Crown } from "lucide-react";

const DISMISS_KEY = "hc_trial_banner_dismissed";

/**
 * Shown on Dashboard when user is in their 14-day Premium trial.
 * Displays days left, benefits, and upgrade CTA. Also surfaces the
 * Founding Member offer ($14.99/mo locked forever) when spots are open
 * so trialing users have a strong reason to lock in before converting.
 * Dismissible (per browser session).
 */
export default function TrialBanner({ user }) {
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch (e) { return false; }
  });
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    if (!user?.is_trial) return;
    api.get("/public/founding-status")
      .then((r) => setFounding(r.data))
      .catch(() => setFounding({}));
  }, [user?.is_trial]);

  if (!user?.is_trial || !user?.trial_ends_at || dismissed) return null;

  const ends = new Date(user.trial_ends_at);
  const now = new Date();
  const msLeft = ends - now;
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));

  // If trial already ended (clock skew or pending scheduler), hide the banner
  if (msLeft <= 0) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  const urgent = daysLeft <= 3;
  const foundingOpen = founding?.is_open && founding?.spots_remaining > 0;
  const nextFounderNum = founding?.next_founder_number;

  return (
    <div
      className={`hc-card p-6 mb-8 relative overflow-hidden border-2 ${
        urgent
          ? "bg-gradient-to-br from-[#FBEFE9] to-white border-[#C87B67]"
          : "bg-gradient-to-br from-[#E8DCC8]/40 to-white border-[#D9A441]/40"
      }`}
      data-testid="trial-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[#EBE5DF] text-[#7A736E]"
        title="Hide for this session"
        data-testid="trial-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles className={`w-4 h-4 ${urgent ? "text-[#C87B67]" : "text-[#D9A441]"}`} />
        <span className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">
          Premium trial
        </span>
      </div>

      <h2 className="font-display text-2xl mb-1" data-testid="trial-banner-days-left">
        {daysLeft === 0
          ? "Your trial ends today."
          : daysLeft === 1
          ? "1 day left in your Premium trial."
          : `${daysLeft} days left in your Premium trial.`}
      </h2>
      <p className="text-sm text-[#7A736E] mb-4 max-w-2xl">
        You&apos;re trying every Premium feature free for 14 days — no credit card needed. After your trial, you&apos;ll automatically move to the Free plan (one Hope Chest, no SMS, no sharing). Your data always stays intact.
      </p>

      {foundingOpen && (
        <div
          className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-[#3A5A4A] to-[#2C4538] text-white relative overflow-hidden"
          data-testid="trial-founder-offer"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#E8DCC8] flex items-center justify-center flex-shrink-0">
              <Crown className="w-4 h-4 text-[#3A5A4A]" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] text-[#E8DCC8] mb-0.5">
                Founding member offer — unlocked during your trial
              </p>
              <p className="font-display text-lg leading-tight" data-testid="trial-founder-line">
                Lock in <strong>$14.99/mo forever</strong>
                {typeof nextFounderNum === "number" && (
                  <> &middot; you&apos;d be <strong>Founder #{nextFounderNum}</strong></>
                )}
              </p>
              <p className="text-xs text-white/80 mt-1">
                Only <strong>{founding.spots_remaining}</strong> of {founding.total_spots} founding spots remaining. Price locked for life, never increases.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-5 text-sm text-[#3D3935]">
        {[
          "Unlimited Hope Chests",
          "Family sharing & co-caregiving",
          "SMS medication reminders",
          "Photo Timeline & PDF export",
          "Weekly care-team reports",
          "Doctor visit summaries",
        ].map((b) => (
          <div key={b} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#3A5A4A] flex-shrink-0 mt-0.5" />
            <span>{b}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => nav("/pricing")}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white px-6 py-3 text-sm font-medium transition"
          data-testid="trial-banner-upgrade-cta"
        >
          {foundingOpen
            ? `Claim Founder #${nextFounderNum ?? ""} at $14.99/mo`
            : urgent
            ? "Upgrade before it ends"
            : "See plans & lock in pricing"}
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={dismiss}
          className="inline-flex items-center justify-center rounded-full border border-[#EBE5DF] hover:bg-[#F3EFEA] text-[#7A736E] px-6 py-3 text-sm transition"
          data-testid="trial-banner-later"
        >
          Remind me later
        </button>
      </div>
    </div>
  );
}
